/**
 * Typed API client for the admin app. Reads session token from localStorage.
 */
import {
  DEFAULT_API_BASE,
  SESSION_TOKEN_KEY,
  SESSION_TYPE_KEY,
} from '@xiaopeiban/shared';
import type {
  ApiError,
  AuthSessionRes,
  ChildDashboard,
  ChildSummary,
  Chore,
  LearningPlan,
  Parent,
  Breed,
} from '@xiaopeiban/shared';

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) || DEFAULT_API_BASE;

export function getToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}
export function setSession(token: string, type: 'parent' | 'child'): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
  localStorage.setItem(SESSION_TYPE_KEY, type);
}
export function clearSession(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_TYPE_KEY);
}

export class ApiCallError extends Error {
  code: string;
  status: number;
  detail?: unknown;
  constructor(code: string, message: string, status: number, detail?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data as ApiError).error) {
    const e = (data as ApiError).error;
    throw new ApiCallError(
      e?.code || 'UNKNOWN',
      e?.message || `HTTP ${res.status}`,
      res.status,
      e?.detail,
    );
  }
  return data as T;
}

// ─── Auth ──────────────────────────────────────────────────────────────────

export const api = {
  // auth
  requestLoginCode(email: string) {
    return call<{ ok: true; devCode?: string }>(
      '/api/v1/auth/parent/request-login-code',
      { method: 'POST', body: JSON.stringify({ email }) },
    );
  },
  verifyLoginCode(email: string, code: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/verify-login-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },
  loginWithPassword(email: string, password: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  parentRegisterStart(email: string, password: string) {
    return call<{ ok: true; message: string; devCode?: string }>(
      '/api/v1/auth/parent/register-start',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    );
  },
  parentRegisterComplete(email: string, code: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/register-complete', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },
  setPassword(password: string) {
    return call<{ ok: true }>('/api/v1/auth/parent/set-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },
  logout() {
    return call<{ ok: true }>('/api/v1/auth/logout', { method: 'POST' });
  },

  // parent
  getMe() {
    return call<{ parent: Parent }>('/api/v1/parent/me');
  },
  pushPublicKey() {
    return call<{ public_key: string | null; enabled: boolean }>('/api/v1/parent/push/public-key');
  },
  pushSubscribe(subscription: PushSubscriptionJSON) {
    return call<{ ok: true }>('/api/v1/parent/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription }),
    });
  },
  pushUnsubscribe(endpoint: string) {
    return call<{ ok: true }>('/api/v1/parent/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  },
  listChildren() {
    return call<{
      children: ChildSummary[];
      quota: { used: number; max: number };
    }>('/api/v1/parent/children');
  },
  createChild(body: { name: string; password: string; birth_date?: string | null; avatar?: string | null }) {
    return call<{
      ok: true;
      child: { id: number; name: string; avatar: string | null; birth_date: string | null };
    }>('/api/v1/parent/children', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  setVoiceBottleShare(childId: number, enable: boolean) {
    return call<{ ok: true; voice_bottle_share: number }>(
      `/api/v1/parent/children/${childId}/voice-bottle-share`,
      { method: 'POST', body: JSON.stringify({ enable }) },
    );
  },
  listChildVoiceBottles(childId: number) {
    return call<{
      share_enabled: boolean;
      bottles: Array<{
        id: number; transcript: string; duration_ms: number;
        mime: string; audio_b64: string; created_at: number;
      }>;
    }>(`/api/v1/parent/children/${childId}/voice-bottles`);
  },
  childDashboard(childId: number) {
    return call<ChildDashboard>(`/api/v1/parent/children/${childId}/dashboard`);
  },
  updateChildProfile(childId: number, body: { birth_date?: string | null; avatar?: string | null }) {
    return call<{ ok: true }>(`/api/v1/parent/children/${childId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  grantPoints(childId: number, amount: number, reason?: string) {
    return call<{ ok: true; transaction_id: number }>(
      `/api/v1/parent/children/${childId}/grant-points`,
      { method: 'POST', body: JSON.stringify({ amount, reason }) },
    );
  },

  listChores(childId: number) {
    return call<{ chores: Chore[] }>(`/api/v1/parent/chores?child_id=${childId}`);
  },
  createChore(body: {
    child_id: number; title: string; description?: string; points: number;
    frequency: 'daily' | 'weekly' | 'once'; weekdays?: string; auto_approve?: boolean;
    daily_deadline_hour?: number | null; gates_play?: boolean;
  }) {
    return call<{ ok: true; id: number }>('/api/v1/parent/chores', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateChore(id: number, body: Partial<Chore>) {
    return call<{ ok: true }>(`/api/v1/parent/chores/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  deleteChore(id: number) {
    return call<{ ok: true }>(`/api/v1/parent/chores/${id}`, {
      method: 'DELETE',
    });
  },

  decideChoreCompletion(id: number, reject: boolean, reason?: string) {
    return call<{ ok: true }>(
      `/api/v1/parent/chore-completions/${id}/decide`,
      { method: 'POST', body: JSON.stringify({ reject, reason }) },
    );
  },
  decideTaskSubmission(id: number, reject: boolean, reason?: string) {
    return call<{ ok: true }>(
      `/api/v1/parent/task-submissions/${id}/decide`,
      { method: 'POST', body: JSON.stringify({ reject, reason }) },
    );
  },
  decideRewardRequest(id: number, reject: boolean, amount_approved?: number, reason?: string) {
    return call<{ ok: true }>(
      `/api/v1/parent/reward-requests/${id}/decide`,
      { method: 'POST', body: JSON.stringify({ reject, amount_approved, reason }) },
    );
  },

  // 悄悄话 — parent silently puts a line in the pet's mouth.
  listWhispers(childId: number) {
    return call<{
      whispers: Array<{
        id: number; text: string; created_at: number;
        delivered_at: number | null; expires_at: number;
        cancelled_at: number | null;
      }>;
    }>(`/api/v1/parent/children/${childId}/whispers`);
  },
  createWhisper(childId: number, text: string) {
    return call<{ ok: true; id: number }>(
      `/api/v1/parent/children/${childId}/whispers`,
      { method: 'POST', body: JSON.stringify({ text }) },
    );
  },
  cancelWhisper(id: number) {
    return call<{ ok: true }>(`/api/v1/parent/whispers/${id}`, { method: 'DELETE' });
  },

  childTimeline(childId: number) {
    return call<{
      events: Array<{
        id: string; type: string; at: number;
        icon: string; title: string; body?: string | null;
      }>;
    }>(`/api/v1/parent/children/${childId}/timeline`);
  },

  getLearningPlan(childId: number) {
    return call<{ plan: LearningPlan | null }>(
      `/api/v1/parent/learning-plan?child_id=${childId}`,
    );
  },
  setLearningPlan(childId: number, config: LearningPlan['config']) {
    return call<{ ok: true }>('/api/v1/parent/learning-plan', {
      method: 'PUT',
      body: JSON.stringify({ child_id: childId, config }),
    });
  },

  listBreeds() {
    return call<{ breeds: Breed[] }>('/api/v1/parent/breeds');
  },
  updateBreed(id: number, body: { price?: number; active?: boolean; description?: string }) {
    return call<{ ok: true }>(`/api/v1/parent/breeds/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  // AI directives
  listDirectives(childId: number, includeArchived = false) {
    return call<{ directives: Directive[] }>(
      `/api/v1/parent/directives?child_id=${childId}${includeArchived ? '&include_archived=1' : ''}`,
    );
  },
  createDirective(body: {
    child_id: number; category: string; content: string;
    priority?: number; max_surface?: number; active_to?: number | null;
  }) {
    return call<{ ok: true; id: number }>('/api/v1/parent/directives', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateDirective(id: number, body: Partial<Directive>) {
    return call<{ ok: true }>(`/api/v1/parent/directives/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  deleteDirective(id: number) {
    return call<{ ok: true }>(`/api/v1/parent/directives/${id}`, {
      method: 'DELETE',
    });
  },
  chatHistory(childId: number, limit = 100) {
    return call<{ messages: ChatHistoryMsg[] }>(
      `/api/v1/parent/chat-history?child_id=${childId}&limit=${limit}`,
    );
  },

  // Parent challenges
  listChallenges(childId: number) {
    return call<{ challenges: ParentChallenge[] }>(`/api/v1/parent/challenges?child_id=${childId}`);
  },
  createChallenge(body: {
    child_id: number; title: string; description?: string;
    icon?: string; cost?: number; reward_points?: number;
    penalty_points?: number; deadline?: number;
  }) {
    return call<{ ok: true; id: number }>('/api/v1/parent/challenges', {
      method: 'POST', body: JSON.stringify(body),
    });
  },
  deleteChallenge(id: number) {
    return call<{ ok: true }>(`/api/v1/parent/challenges/${id}`, { method: 'DELETE' });
  },
};

export interface ParentChallenge {
  id: number;
  title: string;
  description: string | null;
  icon: string | null;
  cost: number;
  reward_points: number;
  penalty_points: number;
  deadline: number | null;
  resolved_at: number | null;
  outcome: string | null;
  created_at: number;
}

export interface Directive {
  id: number;
  category: 'teaching' | 'reminder' | 'encouragement' | 'boundary' | 'topic_to_raise' | 'topic_to_avoid';
  content: string;
  priority: number;
  active_from: number | null;
  active_to: number | null;
  times_surfaced: number;
  max_surface: number | null;
  created_at: number;
  deleted_at: number | null;
}

export interface ChatHistoryMsg {
  id: number;
  thread_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: number;
  pet_id: number;
  pet_nickname: string;
  species: string;
  breed_name: string;
}
